import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiSearchControlModule } from '@snelstart/ui-search-control';
import { ToastService } from '@snelstart/ui-toast';
import { Observable, Subject, takeUntil } from 'rxjs';
import {
    Aangifte,
    AangifteBerekening,
    AllowanceEntity,
    BokkzClient,
    BtwAangifte,
    BtwBerekening,
    BtwOverzicht,
    BtwOverzichtFrequentie,
    ClientEntity,
    CorporateTaxEntity,
    DividendTaxEntity,
    GiftTaxEntity,
    IncomeTaxEntity,
    ValueAddedTaxEntity,
    VerzendGegevens,
    VerzendGegevensActie,
    VpbBerekening,
} from '../services/api.bokkz.services';
import { BokkzIbDataService } from '../services/bokkz-ib-data-service';
import { BokkzTslDataService } from '../services/bokkz-tsl-data-service';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { BokkzRapportageClient, JaarrapportageEntity, LegalSizeCriteriaValue } from '../services/api.bokkz.jaarstukken';
import { BokkzJrLogic } from '../services/bokkz-jr-logic';

@Component({
  selector: 'snelstart-venture-fiscaal-domain-logic-verzenden',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SharedUiButtonModule,
    SharedUiDetailsModule,
    SharedUiDialogModule,
    SharedUiFormControlsModule,
    SharedUiSearchControlModule,
    SharedUiAlertModule,
    DecimalPipe,
  ],
  templateUrl: './verzenden.component.html',
  styleUrl: './verzenden.component.css'
})
export class VerzendenComponent implements OnChanges, OnDestroy {

  @Input() public id: any;
  @Input() public type: any;
  @Input() public validaties: any;
  @Input() public naam: any;
  @Input() public naamPartner: any;
  @Input() public nummer: any;
  @Input() public start: any;
  @Input() public eind: any;
  @Input() public bedrag: any;
  @Input() public year: any;
  @Input() public soort: any;
  @Input() public isDefAangifte = {} as boolean;
  @Input() public client = {} as ClientEntity;
  @Input() public entity = {} as
      | AllowanceEntity
      | CorporateTaxEntity
      | DividendTaxEntity
      | GiftTaxEntity
      | IncomeTaxEntity
      | JaarrapportageEntity
      | ValueAddedTaxEntity
  @Input() public berekening = {} as AangifteBerekening | VpbBerekening | BtwBerekening;
  @Input() public dialogVisible = false;
  @Output() public dialogVisibleChange = new EventEmitter<boolean>();

  private destroy$ = new Subject<void>();
  public clientEntity = {} as ClientEntity;
  public currentYear = new Date().getFullYear();
  public optionsVoorlopigDefinitief: SelectFormControlItem<string>[] = [];
  public optionsBelastingplichtige: SelectFormControlItem<number>[] = [];
  public optionsToeslagen: SelectFormControlItem<string>[] = [];
  public optionsOmzetbelasting: SelectFormControlItem<BtwAangifte>[] = [];
  public optionsIcp: SelectFormControlItem<string>[] = [];
  public optionsSuppletieBtw: SelectFormControlItem<BtwAangifte>[] = [];
  public submission = {} as FormGroup;
  public submissionPartner = {} as FormGroup;
  public toeslagenFormGroup = {} as FormGroup;
  public publicatieFormGroup = {} as FormGroup;
  public verzendenGezamenlijk = false;
  public allowanceYear: any;
  public teVerzendenAangifteBtw = {} as BtwAangifte;
  public showDateErrorAllowance = false;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' }
  ];

  public readonly indienenButtonConfig = ButtonConfiguration.create({
    onClick: () =>
      new Observable((subscriber) => {

        if (this.type === 'VPB') {
          this.verzendenVpb();
        }
        if (this.type === 'IB') {
          this.verzendenIb();
        }
        if (this.type === 'TSL') {
          this.verzendenTsl();
        }
        if (this.type === 'DB') {
          this.verzendenDividendTax();
        }
        if (this.type === 'SB') {
          this.verzendenSb();
        }
        if (this.type === 'BTW') {
          this.verzendenBtw();
        }
        if (this.type === 'JR') {
          this.verzendenJaarrapportage();
        }
        setTimeout(() => {
          this.dialogVisibleChange.emit(false);
          subscriber.next(true);
          subscriber.complete();
        }, 2000);
      })
  });

  public get isDocumentResubmissionDueToTechnicalOrProcess(): boolean {
    return (
      this.publicatieFormGroup?.get('documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuracies')?.get('value')?.value === true
    );
  }
  public get isNullOrEmptyDocumentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation(): boolean {
    const value = this.publicatieFormGroup?.get('documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation')?.get('value')?.value
    return value == null || value.trim() === '';
  }

  constructor(
    private bokkzClient: BokkzClient,
    public bokkzRapportageClient: BokkzRapportageClient,
    public dataService: BokkzIbDataService,
    public algDataService: BokkzAlgDataService,
    public tslDataService: BokkzTslDataService,
    private bokkzJrLogic: BokkzJrLogic,
    private fb: FormBuilder,
    private readonly toastService: ToastService
  )
  {
      const fiscal = this.entity.taxPayerPartnerFiscalNumber;
      const fallback = this.entity.aangifte?.partnerAangifte?.aangever?.bsn;

      const bsnPartner = fiscal != null && fiscal > 8 ? fiscal : fallback;
      let naamPartner ={} as string
      let naam ={} as string;
      let bedrag = {} as string
      const __ret = this.bepaalNaamBedrag(bedrag, naam, naamPartner);
      bedrag = __ret.bedrag;
      naam = __ret.naam;
      naamPartner = __ret.naamPartner
      this.submission = this.fb.group({
      submissionPerson: 1,
      submissionName: '',
      submissionType: '',
      naam: naam,
      naamPartner: naamPartner,
      nummer: this.nummer,
      start: this.start,
      eind: this.eind,
      bedrag: bedrag,
      year: this.year,
      soortAangifte: this.soort,
      teVerzendenAangifteBtw: null as unknown as BtwAangifte
      });

      this.submissionPartner = this.fb.group({
      submissionPerson: 1,
      submissionName: '',
      submissionType: '',
      naam: naam,
      naamPartner: naamPartner,
      nummer: bsnPartner,
      start: this.start,
      eind: this.eind,
      bedrag: bedrag,
      year: this.year,
      soortAangifte: this.soort
      });

      this.toeslagenFormGroup = this.fb.group<VerzendGegevens>({
      actie: null as unknown as VerzendGegevensActie,
      ingangsdatumWijziging: null as unknown as Date,
      kenmerk: null as unknown as string,
      year: this.allowanceYear
      });

      this.publicatieFormGroup = this.fb.group({
      documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuracies: this.fb.group({
        value: false
      }),
      documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation: this.fb.group({
        value: null as unknown as string
      })
    });
  }

  private bepaalNaamBedrag = (bedrag: string, naam: string, naamPartner: string) => {
      if (this.type === 'IB') {
          const aangifteBerekening = this.berekening as AangifteBerekening;
          const verschuldigdeInkomstenbelastingAangever = aangifteBerekening.verschuldigdeInkomstenbelasting || 0;
          const zvwAangever = aangifteBerekening.verschuldigdeZVW || 0;
          const revisieRenteAangever = this.entity.aangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente || 0
          const verschuldigdeInkomstenbelastingPartner = aangifteBerekening.partnerBerekening?.verschuldigdeInkomstenbelasting || 0;
          const zvwPartner = aangifteBerekening.partnerBerekening?.verschuldigdeZVW || 0;
          const revisieRentePartner = this.entity.aangifte?.partnerAangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente || 0
          const formatNaam = (aangever?: any) => {
              if (!aangever) return '';

              const parts: string[] = [];

              if (aangever.achternaam) {
                  parts.push(aangever.achternaam);
              }

              if (aangever.tussenvoegsels) {
                  parts.push(aangever.tussenvoegsels);
              }

              if (aangever.voorletters) {
                  parts.push(`${aangever.voorletters}.`);
              }

              return parts.join(', ');
          };

          bedrag = verschuldigdeInkomstenbelastingAangever + zvwAangever + revisieRenteAangever + verschuldigdeInkomstenbelastingPartner + zvwPartner + revisieRentePartner
          naam = formatNaam(this.entity.aangifte?.aangever);
          naamPartner = formatNaam(this.entity.aangifte?.partnerAangifte?.aangever);
      } else {
          naam = this.naam;
          bedrag = this.bedrag
      }
      return { bedrag, naam, naamPartner };
  }

  onChangeSelected() {
    const waarde = this.submission.get('submissionPerson')?.value;
    if (waarde) {
        const fiscal = this.entity.taxPayerPartnerFiscalNumber;
        const fallback = this.entity.aangifte?.partnerAangifte?.aangever?.bsn;
        const bsnPartner = fiscal != null && fiscal > 8 ? fiscal : fallback;

        let naamPartner ={} as string
        let naam ={} as string;
        let testBedrag = {} as string
        const __ret = this.bepaalNaamBedrag(testBedrag, naam, naamPartner);
        naam = __ret.naam;
        naamPartner = __ret.naamPartner

        const aangifteBerekening = this.berekening as AangifteBerekening;
        const verschuldigdeInkomstenbelastingAangever = aangifteBerekening.verschuldigdeInkomstenbelasting || 0;
        const zvwAangever = aangifteBerekening.verschuldigdeZVW || 0;
        const revisieRenteAangever = this.entity.aangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente || 0
        const verschuldigdeInkomstenbelastingPartner = aangifteBerekening.partnerBerekening?.verschuldigdeInkomstenbelasting || 0;
        const zvwPartner = aangifteBerekening.partnerBerekening?.verschuldigdeZVW || 0;
        const revisieRentePartner = this.entity.aangifte?.partnerAangifte?.box1?.belastbarePeriodiekeUitkeringenEnVerstrekkingen?.revisierente || 0
        const bedrag = verschuldigdeInkomstenbelastingAangever + zvwAangever + revisieRenteAangever+verschuldigdeInkomstenbelastingPartner+zvwPartner+revisieRentePartner

        switch (waarde as number) {
        case 1:
          this.submission.patchValue({
            naam: naam,
            nummer: this.entity.taxPayerFiscalNumber,
            start: this.start,
            eind: this.eind,
            year: this.year,
            bedrag: bedrag,
          });
          this.submissionPartner.patchValue({
            naam: naamPartner,
            nummer: bsnPartner,
            start: this.start,
            eind: this.eind,
            year: this.year,
            soortAangifte: 'IB',
            bedrag:
              this.berekening.partnerBerekening.verschuldigdeInkomstenbelasting +
              this.berekening.partnerBerekening.verschuldigdeZVW +revisieRentePartner
          });
          this.verzendenGezamenlijk = true;
          break;
        case 2:
          this.verzendenGezamenlijk = false;
          this.submission.patchValue({
            naam: naam,
            nummer: this.entity.aangifte.aangever.bsn,
            start: this.start,
            eind: this.eind,
            year: this.year,
            bedrag: this.berekening.verschuldigdeInkomstenbelasting + this.berekening.verschuldigdeZVW + revisieRenteAangever
          });
          break;
        case 3:
          this.verzendenGezamenlijk = false;
          this.submission.patchValue({
            naam: naamPartner,
            nummer: bsnPartner,
            start: this.start,
            eind: this.eind,
            year: this.year,
            bedrag:
              this.berekening.partnerBerekening.verschuldigdeInkomstenbelasting +
              this.berekening.partnerBerekening.verschuldigdeZVW + revisieRentePartner
          });
          break;
      }
    }
  }

  onChangeVatSelected() {
    const waarde = this.submission.get('teVerzendenAangifteBtw')?.value;
    if (waarde) {
      this.teVerzendenAangifteBtw = waarde;
      this.submission.patchValue({ bedrag: waarde.totaal }, { emitEvent: false });
    }
  }

  onChangeIcpSelected() {
    const index = this.submission.get('teVerzendenAangifteBtw')?.value;
    const valueAddedTaxEntity = sessionStorage.getItem('valueAddedTaxEntity');
    if (valueAddedTaxEntity && index) {
      const vatEntity = this.entity as ValueAddedTaxEntity;
      if (vatEntity?.overzicht?.icpAangiftes?.length === 4) {
        if (index === "1" || index === "2" || index === "3") {
          vatEntity.overzicht.icpAangiftes.at(0)!.verzenden = true;
          sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(vatEntity));
        }
        if (index === "4" || index === "5" || index === "6") {
          vatEntity.overzicht.icpAangiftes.at(1)!.verzenden = true;
          sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(vatEntity));
        }
        if (index === "7" || index === "8" || index === "9") {
          vatEntity.overzicht.icpAangiftes.at(2)!.verzenden = true;
          sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(vatEntity));
        }
        if (index === "10" || index === "11" || index === "12") {
          vatEntity.overzicht.icpAangiftes.at(3)!.verzenden = true;
          sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(vatEntity));
        }
      }
      if (vatEntity?.overzicht?.icpAangiftes?.at(index-1)) {
        vatEntity.overzicht.icpAangiftes.at(index-1)!.verzenden = true;
        sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(vatEntity));
      }
    }
  }

  ngOnChanges(): void {
      const fiscal = this.entity.taxPayerPartnerFiscalNumber;
      const fallback = this.entity.aangifte?.partnerAangifte?.aangever?.bsn;
      let naam ={} as string;
      let bedrag = {} as string
      let naamPartner = {} as string
      const bsnPartner = fiscal != null && fiscal > 8 ? fiscal : fallback;
      const __ret = this.bepaalNaamBedrag(bedrag, naam,naamPartner);
      bedrag = __ret.bedrag;
      naam = __ret.naam;
      naamPartner = __ret.naamPartner
      this.submission.reset();
      this.submission.patchValue({ submissionPerson: 1 }, { emitEvent: false });
      this.submission.patchValue({ naam: naam }, { emitEvent: false });
      this.submission.patchValue({ naamPartner: naamPartner }, { emitEvent: false });
      this.submission.patchValue({ nummer: this.nummer }, { emitEvent: false });
      this.submissionPartner.patchValue({ nummer:bsnPartner }, { emitEvent: false });
      this.submission.patchValue({ start: this.start }, { emitEvent: false });
      this.submission.patchValue({ eind: this.eind }, { emitEvent: false });
      this.submission.patchValue({ bedrag: bedrag }, { emitEvent: false });
      this.submission.patchValue({ year: this.year }, { emitEvent: false });
      this.submission.patchValue({ soortAangifte: this.type }, { emitEvent: false });

      this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.clientEntity = value;
          if (this.type !== 'JR') this.submission.patchValue({ nummer: value.taxId }, { emitEvent: false });
      });
      this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
          this.currentYear = value;
          this.submission.patchValue({ year: value }, { emitEvent: false })
      });

      if (this.type === 'VPB') {
      this.optionsVoorlopigDefinitief = [
        {
          value: 'voorlopig',
          label: 'Verzoek wijziging of wijziging aanslag',
          disabled: this.isDefAangifte
        },
        {
          value: 'definitief',
          label: 'Definitieve aangifte',
          disabled: !this.isDefAangifte
        }
      ];
    }

      if (this.type === 'IB') {
      this.optionsVoorlopigDefinitief = [
        {
          value: 'voorlopig',
          label: 'Verzoek wijziging of wijziging aanslag',
          disabled: this.isDefAangifte
        },
        {
          value: 'definitief',
          label: 'Definitieve aangifte',
          disabled: !this.isDefAangifte
        },
        {
          value: 'owr',
          label: 'Opgaaf werkelijk rendement',
          disabled: true
        }
      ];
    }

      if (this.type === 'IB') {
      if (this.entity.aangifte.gezamenlijkeAangifte === true) {
        this.optionsBelastingplichtige = [
          { value: 1, label: 'Aangever & partner' },
          { value: 2, label: 'Aangever' },
          { value: 3, label: 'Partner' }
        ];
      } else {
        this.submission.patchValue({ submissionPerson: 2 }, { emitEvent: false });
        this.optionsBelastingplichtige = [{ value: 2, label: 'Aangever' }];
      }
    }

      if (this.type === 'TSL') {
      this.optionsToeslagen = [
        {
          value: VerzendGegevensActie.AANVRAAG_ZORGTOESLAG,
          label: 'Aanvraag zorgtoeslag'
        },
        {
          value: VerzendGegevensActie.STOP_ZORGTOESLAG,
          label: `Stopzetten zorgtoeslag`
        },
        {
          value: VerzendGegevensActie.WIJZIG_TOETINGSINKOMEN_AANGEVER,
          label: `Wijzigen toetsinkomen aangever`
        },
        {
          value: VerzendGegevensActie.WIJZIG_TOETINGSINKOMEN_PARTNER,
          label: `Wijzigen toetsinkomen partner`
        },
        {
          value: VerzendGegevensActie.AANVRAAG_KOT,
          label: 'Aanvraag kinderopvangtoeslag',
          disabled: !this.tslDataService.hasKinderen.value
        },
        {
          value: VerzendGegevensActie.WIJZIG_KOT,
          label: `Wijzigen gegevens kinderopvang`,
          disabled: !this.tslDataService.hasKinderen.value
        },
        {
          value: VerzendGegevensActie.STOP_KOT,
          label: `Stopzetten kinderopvangtoeslag`,
          disabled: !this.tslDataService.hasKinderen.value
        },
        {
          value: VerzendGegevensActie.AANVRAAG_HUURTOESLAG,
          label: 'Aanvraag huurtoeslag',
          disabled: !this.tslDataService.hasHuurwoningen.value
        },
        {
          value: VerzendGegevensActie.WIJZIG_HUURTOESLAG,
          label: `Wijzigen gegevens huurwoning`,
          disabled: !this.tslDataService.hasHuurwoningen.value
        },
        {
          value: VerzendGegevensActie.STOP_HUURTOESLAG,
          label: `Stopzetten huurtoeslag`,
          disabled: !this.tslDataService.hasHuurwoningen.value
        }
      ];
    }

      if (this.type === 'BTW') {
      this.teVerzendenAangifteBtw = {} as BtwAangifte;
      const overzicht = this.entity.overzicht as BtwOverzicht;
      if (overzicht) {
        this.optionsVoorlopigDefinitief = [
          {
            value: 'aangifte',
            label: 'Aangifte BTW',
            disabled: false
          },
          {
            value: 'opgaaf',
            label: 'Opgaaf ICP',
            disabled: false
          },
          {
            value: 'suppletie',
            label: 'Suppletie aangifte',
            disabled: false
          }
        ];
        this.optionsIcp = [
          {
            value: "1",
            label: 'Januari ' + this.year
          },
          {
            value: "2",
            label: 'Februari ' + this.year,
            // kan alleen na 28/29 februari en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 2, 1) > new Date(new Date().toDateString())
          },
          {
            value: "3",
            label: 'Maart ' + this.year,
            // kan alleen na 31 maart en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 3, 1) > new Date(new Date().toDateString())
          },
          {
            value: "4",
            label: 'April ' + this.year,
            // kan alleen na 30 april en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 4, 1) > new Date(new Date().toDateString())
          },
          {
            value: "5",
            label: 'Mei ' + this.year,
            // kan alleen na 31 mei en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 5, 1) > new Date(new Date().toDateString())
          },
          {
            value: "6",
            label: 'Juni ' + this.year,
            // kan alleen na 30 juni en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 6, 1) > new Date(new Date().toDateString())
          },
          {
            value: "7",
            label: 'Juli ' + this.year,
            // kan alleen na 31 juli en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 7, 1) > new Date(new Date().toDateString())
          },
          {
            value: "8",
            label: 'Augustus ' + this.year,
            // kan alleen na 31 augustus en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 8, 1) > new Date(new Date().toDateString())
          },
          {
            value: "9",
            label: 'September ' + this.year,
            // kan alleen na 30 september en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 9, 1) > new Date(new Date().toDateString())
          },
          {
            value: "10",
            label: 'Oktober ' + this.year,
            // kan alleen na 31 oktober en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 10, 1) > new Date(new Date().toDateString())
          },
          {
            value: "11",
            label: 'November ' + this.year,
            // kan alleen na 30 november en als aangifte nog niet verzonden is
            disabled: new Date(this.year, 11, 1) > new Date(new Date().toDateString())
          },
          {
            value: "12",
            label: 'December ' + this.year,
            // kan alleen na 31 december en als aangifte nog niet verzonden is
            disabled: new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString())
          }
        ];
        if (overzicht.frequentie === BtwOverzichtFrequentie.MAAND) {
          this.optionsOmzetbelasting = [
            {
              value: overzicht.btwAangiftes[0],
              label: 'Januari ' + this.year
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              // disabled:
              //   new Date(this.year, 1, 1) >
              //     new Date(new Date().toDateString()) ||
              //   overzicht.btwAangiftes[0].verzendDatum != undefined,
            },
            {
              value: overzicht.btwAangiftes[1],
              label: 'Februari ' + this.year,
              // kan alleen na 28/29 februari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 2, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[1].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[2],
              label: 'Maart ' + this.year,
              // kan alleen na 31 maart en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 3, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[2].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[3],
              label: 'April ' + this.year,
              // kan alleen na 30 april en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 4, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[3].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[4],
              label: 'Mei ' + this.year,
              // kan alleen na 31 mei en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 5, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[4].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[5],
              label: 'Juni ' + this.year,
              // kan alleen na 30 juni en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 6, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[5].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[6],
              label: 'Juli ' + this.year,
              // kan alleen na 31 juli en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 7, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[6].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[7],
              label: 'Augustus ' + this.year,
              // kan alleen na 31 augustus en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 8, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[7].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[8],
              label: 'September ' + this.year,
              // kan alleen na 30 september en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 9, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[8].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[9],
              label: 'Oktober ' + this.year,
              // kan alleen na 31 oktober en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 10, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[9].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[10],
              label: 'November ' + this.year,
              // kan alleen na 30 november en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 11, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[10].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[11],
              label: 'December ' + this.year,
              // kan alleen na 31 december en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[11].verzendDatum != undefined
            }
          ];
        }
        if (overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL) {
          this.optionsOmzetbelasting = [
            {
              value: overzicht.btwAangiftes[0],
              label: 'Eerste kwartaal ' + this.year,
              // kan alleen na 31 maart en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 3, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[0].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[1],
              label: 'Tweede kwartaal ' + this.year,
              // kan alleen na 30 juni en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 6, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[1].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[2],
              label: 'Derde kwartaal ' + this.year,
              // kan alleen na 30 september en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 9, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[2].verzendDatum != undefined
            },
            {
              value: overzicht.btwAangiftes[3],
              label: 'Vierde kwartaal ' + this.year,
              // kan alleen na 31 december en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[3].verzendDatum != undefined
            }
          ];
        }
        if (overzicht.frequentie === BtwOverzichtFrequentie.JAAR) {
          this.optionsOmzetbelasting = [
            {
              value: overzicht.btwAangiftes[0],
              label: this.year,
              // kan alleen na 31 december en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[0].verzendDatum != undefined
            }
          ];
        }
        if (overzicht.frequentie === BtwOverzichtFrequentie.MAAND) {
          if (overzicht.btwSuppleties?.at(0)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[0],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[2].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(1)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[1],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[1].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(2)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[2],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[2].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(3)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[3],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[3].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(4)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[4],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[4].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(5)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[5],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[5].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(6)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[6],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[6].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(7)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[7],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[7].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(8)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[8],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[8].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(9)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[9],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[9].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(10)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[10],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[10].verzendDatum != undefined
            });
          }
          if (overzicht.btwSuppleties?.at(11)) {
            this.optionsSuppletieBtw.push({
              value: overzicht.btwSuppleties[11],
              label: 'Suppletie januari ' + this.year,
              // kan alleen na 31 januari en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 1, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[11].verzendDatum != undefined
            });
          }
        }
        if (overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL) {
          this.optionsSuppletieBtw = [
            {
              value: overzicht.btwSuppleties[0],
              label: 'Suppletie eerste kwartaal ' + this.year,
              // kan alleen na 31 maart en als aangifte verzonden is
              disabled:
                new Date(this.year, 3, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[0].verzendDatum == undefined
            },
            {
              value: overzicht.btwSuppleties[1],
              label: 'Suppletie tweede kwartaal ' + this.year,
              // kan alleen na 30 juni en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 6, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[1].verzendDatum == undefined
            },
            {
              value: overzicht.btwSuppleties[2],
              label: 'Suppletie derde kwartaal ' + this.year,
              // kan alleen na 30 september en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 9, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[2].verzendDatum == undefined
            },
            {
              value: overzicht.btwSuppleties[3],
              label: 'Suppletie vierde kwartaal ' + this.year,
              // kan alleen na 31 december en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwAangiftes[3].verzendDatum == undefined
            },
            {
              value: overzicht.btwSuppleties[3],
              label: 'Suppletie ' + this.year,
              // kan alleen na 31 december
              disabled: new Date(this.year + 1, 0, 1) > new Date(new Date().toDateString())
            }
          ];
        }
        if (overzicht.frequentie === BtwOverzichtFrequentie.JAAR) {
          this.optionsSuppletieBtw = [
            {
              value: overzicht.btwSuppleties[0],
              label: 'Suppletie ' + this.year,
              // kan alleen na 31 maart en als aangifte nog niet verzonden is
              disabled:
                new Date(this.year, 3, 1) > new Date(new Date().toDateString()) ||
                overzicht.btwSuppleties[0].verzendDatum != undefined
            }
          ];
        }
        this.optionsSuppletieBtw.push({
          value: overzicht.btwSuppleties[overzicht.btwSuppleties.length -1],
          label: 'Jaarsuppletie ' + overzicht.jaar,
          disabled: overzicht.jaar > new Date().getFullYear()
        })
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTitle() {
    if (this.type === 'JR') return 'Publiceren';
    else if (this.type === 'TSL') return 'Aanvragen toeslagen';
    else if (this.type === 'LB') return 'Indienen aangifte loonheffingen';
    else if (this.type === 'BTW') return 'Indienen aangifte, suppletie-aangifte of opgaaf ICP';
    else if (!this.isDefAangifte) return 'Indienen verzoek';
    else return 'Indienen aangifte';
  }

  public isOptionSelected(): boolean {
    const selectedValue = this.submission?.get('submissionType')?.value;
    const selectedSubmissionPerson = this.submission?.get('submissionPerson')?.value;
    const validatieGeslaagd = this.validaties === 0;
    if (this.type === 'DB' && this.nummer !== null && this.start !== null) {
      return true;
    }
    if (this.type === 'SB') {
      return true;
    }
    if (this.type === 'JR' && this.nummer !== null &&
      ((!this.isDocumentResubmissionDueToTechnicalOrProcess && this.isNullOrEmptyDocumentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation) ||
        (this.isDocumentResubmissionDueToTechnicalOrProcess && !this.isNullOrEmptyDocumentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation))) {
      return true;
    }
    if (this.type === 'BTW' && this.teVerzendenAangifteBtw !== null) {
      return true;
    }
    if (this.type === 'TSL') {
      return this.toeslagenFormGroup.value.actie != undefined && this.toeslagenFormGroup.value.ingangsdatumWijziging != undefined;
    }
    if (this.type === 'IB') {
      if (selectedSubmissionPerson) {
        if (selectedSubmissionPerson == 2) {
          if (this.entity.aangifte.aangever.bsn && this.entity.aangifte.aangever.achternaam && validatieGeslaagd) {
            return true;
          }
        }
        if (selectedSubmissionPerson == 3) {
          if (
            this.entity.aangifte.partnerAangifte?.aangever?.bsn &&
            this.entity.aangifte.partnerAangifte?.aangever?.achternaam &&
            validatieGeslaagd
          ) {
            return true;
          }
        }
        if (selectedSubmissionPerson == 1) {
          if (
            this.entity.aangifte.aangever.bsn &&
            this.entity.aangifte.aangever?.achternaam &&
            this.entity.aangifte.partnerAangifte?.aangever?.bsn &&
            this.entity.aangifte.partnerAangifte?.aangever?.achternaam &&
            validatieGeslaagd
          ) {
            return true;
          }
        }
      }
      return false;
    } else {
      return selectedValue !== null && selectedValue !== undefined && selectedValue !== '';
    }
  }

  private verzendenVpb() {
    const entity = this.entity as CorporateTaxEntity;
    if (entity.sstRelationId !== undefined) {
      this.bokkzClient.postCorporateTax(String(this.entity.sstRelationId), true, !this.isDefAangifte, entity)
        .subscribe({
          next: (response) => {
            if (response && response.aangifte != null) {
              sessionStorage.setItem('corporateTaxEntity', JSON.stringify(response));
              sessionStorage.setItem('vpbAangifte', JSON.stringify(response.aangifte));
              this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst.`);
            }
          },
          error: (err) => {
            console.log('FAILED POST CORPORATE ENTITY');
            console.log(err);
            this.toastService.showErrorMessage(
              $localize`Helaas is er een technisch probleem waardoor de verzending niet succesvol is verlopen. Neem contact op met onze Klantenservice.`
            );
          },
          complete: () => {
            console.log('SUCCES POST CORPORATE ENTITY');
          }
        });
    }
  }

  private verzendenIb() {
    const incomeTaxEntity = this.entity as IncomeTaxEntity;
    incomeTaxEntity.client = this.client;
    const owrSubmission = this.submission?.get('submissionType')?.value === 'owr';
    const selectedSubmissionPerson = this.submission?.get('submissionPerson')?.value;
    if (selectedSubmissionPerson == 1) {
      if (
        incomeTaxEntity.aangifte?.aangever.bsn &&
        incomeTaxEntity.aangifte?.aangever.achternaam &&
        incomeTaxEntity.aangifte?.partnerAangifte?.aangever.bsn &&
        incomeTaxEntity.aangifte?.partnerAangifte?.aangever.achternaam
      ) {
        this.verzendDigipoort(true, false, !this.isDefAangifte, owrSubmission, incomeTaxEntity as IncomeTaxEntity);
        this.verzendDigipoort(true, true, !this.isDefAangifte, owrSubmission, incomeTaxEntity as IncomeTaxEntity);
      }
    }
    if (selectedSubmissionPerson == 2) {
      if (incomeTaxEntity.aangifte?.aangever.bsn && incomeTaxEntity.aangifte.aangever.achternaam) {
        this.verzendDigipoort(true, false, !this.isDefAangifte, owrSubmission, incomeTaxEntity as IncomeTaxEntity);
      }
    }
    if (selectedSubmissionPerson == 3) {
      if (incomeTaxEntity?.aangifte?.partnerAangifte?.aangever.bsn && incomeTaxEntity.aangifte.partnerAangifte.aangever.achternaam) {
        this.verzendDigipoort(true, true, !this.isDefAangifte, owrSubmission, this.entity as IncomeTaxEntity);
      }
    }
  }

  private verzendDigipoort(digipoort: boolean, partner: boolean, provisional: boolean, owr: boolean, incomeTaxEntity: IncomeTaxEntity) {
    this.bokkzClient.postIncomeTax(incomeTaxEntity.sstRelationId!, digipoort, partner, provisional, owr, incomeTaxEntity).subscribe({
      next: (response) => {
        if (response && response.aangifte != null) {
          sessionStorage.setItem('IncomeTaxEntity', JSON.stringify(response));
          sessionStorage.setItem('ibAangifte', JSON.stringify(response.aangifte));
          this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst.`);
        }
      },
      error: (err) => {
        if (this.type === 'VPB') {
          console.log('FAILED POST CORPORATE ENTITY');
        }
        if (this.type === 'IB') {
          console.log('FAILED POST INCOME ENTITY');
        }
        console.log(err);
        this.toastService.showErrorMessage(
          $localize`Helaas is er een technisch probleem waardoor de verzending niet succesvol is verlopen. Neem contact op met onze Klantenservice.`
        );
      },
      complete: () => {
        console.log('SUCCES POST CORPORATE ENTITY');
      }
    });
  }

  private verzendenTsl() {
    const ingangsdatum = this.toeslagenFormGroup.get('ingangsdatumWijziging')?.value as Date;
    const allowanceEntity = this.entity as AllowanceEntity;
    if (allowanceEntity.sstRelationId != undefined && allowanceEntity.toeslagen?.verzendGegevens) {
      allowanceEntity.toeslagen.verzendGegevens.ingangsdatumWijziging = ingangsdatum;
      this.bokkzClient.postAllowance(allowanceEntity.sstRelationId!, true, allowanceEntity).subscribe({
        next: (response) => {
          if (response && response.toeslagen != null) {
            sessionStorage.setItem('allowanceEntity', JSON.stringify(response));
            this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst.`);
          }
        },
        error: (err) => {
          console.log('FAILED POST ALLOWANCE ENTITY');
          console.log(err);
          this.toastService.showErrorMessage(
            $localize`Helaas is er een technisch probleem waardoor de verzending niet succesvol is verlopen. Neem contact op met onze Klantenservice.`
          );
        },
        complete: () => {
          console.log('SUCCES POST ALLOWANCE ENTITY');
        }
      });
    }
  }

  private verzendenBtw() {
    const valueAddedTaxEntity = this.entity as ValueAddedTaxEntity;
    if (valueAddedTaxEntity.sstRelationId !== undefined && valueAddedTaxEntity.overzicht !== undefined) {
      valueAddedTaxEntity.overzicht.teVerzendenAangifte = this.teVerzendenAangifteBtw;
      this.bokkzClient.postValueAddedTax(valueAddedTaxEntity.sstRelationId!, true, valueAddedTaxEntity).subscribe({
        next: (response) => {
          if (response && response.overzicht != null) {
            sessionStorage.setItem('valueAddedTaxEntity', JSON.stringify(response));
            this.toastService.showSuccessMessage($localize`De aangifte wordt ingediend bij de Belastingdienst.`);
          }
        },
        error: (err) => {
          console.log('FAILED POST VALUE ADDED TAX ENTITY');
          console.log(err);
          this.toastService.showErrorMessage(
            $localize`Helaas is er een technisch probleem waardoor de verzending niet succesvol is verlopen. Neem contact op met onze Klantenservice.`
          );
        },
        complete: () => {
          console.log('SUCCES POST VALUE ADDED TAX ENTITY');
        }
      });
    }
  }

  private verzendenDividendTax() {
    const dividendTaxEntity = this.entity as DividendTaxEntity;
    if (dividendTaxEntity.sstRelationId !== undefined) {
      this.bokkzClient.postDividendTax(dividendTaxEntity.sstRelationId, true, dividendTaxEntity).subscribe({
        next: (response) => {
          if (response && response.aangifte != null) {
            sessionStorage.setItem('dividendTaxEntity', JSON.stringify(response));
            this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst.`);
          }
        },
        error: (err) => {
          console.log('FAILED POST DIVIDEND ENTITY');
          console.log(err);
          this.toastService.showErrorMessage(
            $localize`Helaas is er een technisch probleem waardoor de verzending niet succesvol is verlopen. Neem contact op met onze Klantenservice.`
          );
        },
        complete: () => {
          console.log('SUCCES POST DIVIDEND ENTITY');
        }
      });
    }
  }

  private verzendenSb() {
    const entity = this.entity as GiftTaxEntity;
    if (this.entity.sstRelationId !== undefined) {
      this.bokkzClient.postGiftTax(this.entity.sstRelationId, true, entity).subscribe({
        next: (response) => {
          if (response && response.aangifte != null) {
            sessionStorage.setItem('giftTaxEntity', JSON.stringify(response));
            this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst.`);
          }
        },
        error: (err) => {
          console.log('FAILED POST GIFT ENTITY');
          console.log(err);
          this.toastService
            .showSuccessMessage($localize`Het bericht wordt ingediend bij de Belastingdienst. We wachten nog op een retourbericht, wat iets langer kan duren dan gebruikelijk.
        Zodra het retourbericht ontvangen is, wordt dit weergegeven in het dossier.`);
        },
        complete: () => {
          console.log('SUCCES POST GIFT ENTITY');
        }
      });
    }
  }

  private verzendenJaarrapportage() {
    const jr = this.entity as JaarrapportageEntity;
    jr.rapportage = {
      ...jr.rapportage,
      informatieRapportage: {
        ...jr.rapportage?.informatieRapportage,
        documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuracies:
        this.publicatieFormGroup.value.documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuracies,
        documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation:
        this.publicatieFormGroup.value.documentResubmissionDueToTechnicalAndOrProcesRelatedInaccuraciesExplanation
      }
    };

    let entrypointId = '2010';
    if (jr.rapportage.informatieEntiteit?.legalSizeCriteriaClassification.value === LegalSizeCriteriaValue.KLEIN) {
      entrypointId = '2011';
    }

    this.bokkzRapportageClient.postjaarrapportage(String(this.client.sstRelationId), this.year, true, entrypointId, jr).subscribe({
      next: (response) => {
        if (response && response.rapportage != null) {
          sessionStorage.setItem('jrEntity', JSON.stringify(response));
          this.toastService.showSuccessMessage($localize`Het bericht wordt ingediend bij de Kamer van Koophandel.`);
        }
      },
      error: (err) => {
        console.log('FAILED POST JR ENTITY');
        console.log(err);
        this.toastService.showErrorMessage($localize`
          Helaas is er een technisch probleem waardoor het publiceren van de publicatiestukken niet succesvol is verlopen.
          Neem contact op met onze Klantenservice.`);
      },
      complete: () => {
        console.log('SUCCES POST JR ENTITY');
      }
    });
  }

  public saveAllowanceForm() {
    this.entity = this.entity as AllowanceEntity;
    this.entity.toeslagen = {
      ...this.entity.toeslagen,
      verzendGegevens: {
        ...this.entity.toeslagen?.verzendGegevens,
        ingangsdatumWijziging: this.toeslagenFormGroup.value.ingangsdatumWijziging,
        actie: this.toeslagenFormGroup.value.actie,
        kenmerk: this.entity.toeslagen?.bsn
      }
    } as AllowanceEntity;

    if (this.toeslagenFormGroup.value.ingangsdatumWijziging != null) {
      this.checkDatumAllowance();
    }
    this.tslDataService.updateAllowanceTaxEntity(this.entity);
  }

  public checkDatumAllowance() {
    this.entity = this.entity as AllowanceEntity;
    this.allowanceYear = this.entity.year;

    const ingangsdatumWijziging = new Date(this.toeslagenFormGroup.value.ingangsdatumWijziging);
    const startOfYear = new Date(this.allowanceYear, 0, 1); // 1 januari van het jaar
    const endOfYear = new Date(this.allowanceYear, 11, 31); // 31 december van het jaar

    this.showDateErrorAllowance = !(ingangsdatumWijziging >= startOfYear && ingangsdatumWijziging <= endOfYear);
  }

}
